<script setup lang="ts">
import {
  ref,
  computed,
  markRaw,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from 'vue';
import { getAccessToken } from 'axios-jwt';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import {
  loadUserProjects,
  acquireLabelingLock,
  releaseLabelingLock,
  LabelingLockHeldError,
  listLabelingImages,
  getLabelingImageFiles,
  patchLabelingImage,
  putImageAnnotations,
  getImageEmbedding,
  getProjectClasses,
  setRotationLabeling,
  fetchPresignedJson,
  fetchPresignedBinary,
} from '@/api/RobVisionApi';
import {
  type VisionProject,
  type LabelingImage,
  type SamEmbeddingMeta,
  type PerImageCocoDoc,
  LabelingImageStatus,
} from '@/models/RobVision';
import {
  instancesToCocoDoc,
  cocoDocToInstances,
  prelabelsToInstances,
  ensureKeypoint,
  type EditorInstance,
} from '@/helpers/LabelingSerialization';
import { maskBBox, maskHasPixels } from '@/helpers/MaskToPolygon';
import { warmSamDecoder, samDecoderWorker } from '@/helpers/SamDecoderSession';
import AnnotationCanvas, {
  type LabelingToolId,
  type PromptPoint,
} from '@/components/robvision/labeling/AnnotationCanvas.vue';
import ObjectsPanel from '@/components/robvision/labeling/ObjectsPanel.vue';
import DetailsPanel from '@/components/robvision/labeling/DetailsPanel.vue';
import { useAlertsStore } from '@/store/AlertsStore';
import { openRobDocs, ROBDOCS_LABELING_URL } from '@/helpers/RobDocs';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;
const alertsStore = useAlertsStore();

const props = defineProps<{
  projectUuid: string;
  // Image to open first; the first not-ready image when omitted.
  initialImageUuid?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { projectUuid } = props;

const LOCK_HEARTBEAT_MS = 30_000;
const EMBEDDING_POLL_MS = 10_000;
const AUTOSAVE_DEBOUNCE_MS = 3_000;
const UNDO_LIMIT = 10;
// An editor left in a hidden tab this long releases the lock so colleagues
// can work; it re-acquires silently when the tab becomes visible again.
const HIDDEN_RELEASE_MS = 3 * 60_000;

// ---- session state ----
const project = ref<VisionProject | null>(null);
const projectClasses = ref<string[]>([]);
const images = ref<LabelingImage[]>([]);
const currentIndex = ref(0);
const lockedBy = ref<string | null>(null);
const loading = ref(true);

const imageElement = ref<HTMLImageElement | null>(null);
const instances = ref<EditorInstance[]>([]);
const selectedId = ref<number | null>(null);
const prelabelInfo = ref<unknown>(null);

// Pan by default: an editing tool active on entry caused accidental mask
// edits on the first click (roast feedback). Creating an object switches to
// AI segmentation explicitly.
const tool = ref<LabelingToolId>('pan');
// The mask tool the user last drew with — "Object Done" returns to it so the
// next canvas click starts the next object with the same workflow.
const lastMaskTool = ref<LabelingToolId>('sam');
watch(tool, (current) => {
  if (current === 'sam' || current === 'brush' || current === 'polygon') {
    lastMaskTool.value = current;
  }
});
// Half-screen mode: the side panels collapse so the canvas keeps a usable
// width (~750px windows). Starts collapsed on narrow windows.
const panelsCollapsed = ref(window.innerWidth < 900);
const promptMode = ref<'positive' | 'negative'>('positive');
const brushSize = ref(6);
const zoomPct = ref(100);
const polygonCount = ref(0);

const samReady = ref(false);
const samPending = ref(false);
// SAM assist can't work at all (assets missing / worker failed) — distinct
// from "embedding for this image not computed yet".
const samUnavailable = ref(false);
// Worker still downloading the decoder + positional embedding (~20 MB on
// first load) — the embeddings themselves may long be done server-side.
const samModelLoading = ref(false);
const decoding = ref(false);

const dirty = ref(false);
const saving = ref(false);
const lastSavedAt = ref<Date | null>(null);
const saveFailed = ref(false);

const rotationDialog = ref(false);
const rotationEnabled = ref(true);
// True between a hidden-tab lock release and the re-acquire settling —
// edits in that window would save into a 409.
const reacquiringLock = ref(false);

const canvas = ref<InstanceType<typeof AnnotationCanvas> | null>(null);

// Primed at mount and refreshed with the heartbeat: the beforeunload save
// cannot await the token store (the document may unload at the await).
let cachedAuthToken: string | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let embeddingPollTimer: ReturnType<typeof setTimeout> | null = null;
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
let nextInstanceId = 0;
let undoStack: Array<{
  instances: EditorInstance[];
  selectedId: number | null;
}> = [];

const currentImage = computed<LabelingImage | null>(
  () => images.value[currentIndex.value] ?? null,
);
const isDone = computed(
  () => currentImage.value?.status === LabelingImageStatus.Ready,
);
const readyCount = computed(
  () =>
    images.value.filter((im) => im.status === LabelingImageStatus.Ready).length,
);
const selectedInstance = computed(
  () =>
    instances.value.find((instance) => instance.id === selectedId.value) ??
    null,
);
const selectedNumber = computed(() => {
  const index = instances.value.findIndex(
    (instance) => instance.id === selectedId.value,
  );
  return index === -1 ? null : index + 1;
});
const positionText = computed(
  () => `${currentIndex.value + 1} / ${images.value.length}`,
);
const resolutionText = computed(() =>
  imageElement.value
    ? `${imageElement.value.width} × ${imageElement.value.height}`
    : '',
);

// Blocking: the image cannot be marked ready in this state.
const doneHint = computed<string | null>(() => {
  if (
    instances.value.some(
      (instance) => instance.hasMask && !instance.categoryName,
    )
  ) {
    return $gettext('Every object needs a class.');
  }
  return null;
});

// The selected object is finishable: it has a mask and a class (the
// pick-point exists automatically). Drives the "Object Done" action.
const canFinishObject = computed(
  () =>
    !isDone.value &&
    !!selectedInstance.value?.hasMask &&
    !!selectedInstance.value.categoryName,
);

// Informational: marking is allowed, but the user should know what an empty
// image means — images without any object are valid negative examples that
// train the model against false positives.
const emptyHint = computed<string | null>(() =>
  instances.value.some((instance) => instance.hasMask)
    ? null
    : $gettext(
        'No objects labeled — marking confirms this image contains none. It trains the model against false positives.',
      ),
);

const savedText = computed(() => {
  if (saveFailed.value) {
    return $gettext('Autosave failed — retrying');
  }
  if (saving.value) {
    return $gettext('Saving…');
  }
  if (dirty.value) {
    return $gettext('Unsaved changes');
  }
  if (lastSavedAt.value) {
    return $gettext('Saved');
  }
  return '';
});

interface ToolDef {
  id: LabelingToolId;
  icon: string;
  tip: string;
  disabled?: boolean;
}
const tools = computed<ToolDef[]>(() => [
  {
    id: 'sam',
    icon: 'mdi-auto-fix',
    tip: samUnavailable.value
      ? $gettext('AI Segmentation is currently unavailable')
      : samModelLoading.value
        ? $gettext('Loading the AI Segmentation model…')
        : !samReady.value
          ? $gettext('Preparing AI Segmentation…')
          : $gettext('AI Segmentation (S)'),
    // Disabled until this image can actually be segmented.
    disabled: !samReady.value,
  },
  { id: 'brush', icon: 'mdi-brush', tip: $gettext('Brush (B)') },
  { id: 'erase', icon: 'mdi-eraser', tip: $gettext('Eraser (E)') },
  { id: 'polygon', icon: 'mdi-vector-polygon', tip: $gettext('Polygon (P)') },
  { id: 'keypoint', icon: 'mdi-target', tip: $gettext('Pick-point (K)') },
  // Only offered while the project labels rotation.
  ...(rotationEnabled.value
    ? [
        {
          id: 'rotate' as const,
          icon: 'mdi-rotate-right',
          tip: $gettext('Rotation (R)'),
        },
      ]
    : []),
  {
    id: 'pan',
    icon: 'mdi-hand-back-right-outline',
    tip: $gettext('Pan (H or Esc)'),
  },
]);

// ---- lifecycle ----

onMounted(async () => {
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  void getAccessToken().then((token) => {
    cachedAuthToken = token ?? null;
  });
  try {
    const projects = await loadUserProjects(true);
    project.value =
      projects.find((candidate) => candidate.uuid === projectUuid) ?? null;
    if (!project.value) {
      alertsStore.error($gettext('Could not load Vision Project'));
      emit('close');
      return;
    }
    if (project.value.archived) {
      // Belt to the section's guard: never lock or autosave on an archived
      // (read-only) project.
      alertsStore.error($gettext('This project is archived.'));
      emit('close');
      return;
    }

    try {
      await acquireLabelingLock(projectUuid);
    } catch (error) {
      if (error instanceof LabelingLockHeldError) {
        lockedBy.value = error.message;
        loading.value = false;
        return;
      }
      throw error;
    }
    heartbeatTimer = setInterval(heartbeat, LOCK_HEARTBEAT_MS);

    rotationEnabled.value = project.value.rotation_labeling !== false;
    if (
      project.value.rotation_labeling === null ||
      project.value.rotation_labeling === undefined
    ) {
      rotationDialog.value = true;
    }

    void initializeWorker();
    // Classes strictly first: prelabel parsing and the class dropdown need
    // them when the first image opens.
    await loadClasses();
    await loadImages();
  } catch (error) {
    alertsStore.error(
      error instanceof Error ? error.message : $gettext('Loading failed'),
    );
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  window.removeEventListener('pagehide', handlePageHide);
  window.removeEventListener('pageshow', handlePageShow);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  if (hiddenReleaseTimer) {
    clearTimeout(hiddenReleaseTimer);
  }
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
  }
  stopEmbeddingPoll();
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
  }
  // Normal unmount (dialog close): the page is still alive, so the plain
  // request path applies — keepalive with its 64 KiB body cap is only for
  // beforeunload. The lock is released only after the save settles: the
  // annotations PUT requires holding it.
  // The shared decoder worker stays alive for the next editor session —
  // only this editor's listener and its parked embeddings are dropped.
  detachWorkerListener?.();
  samDecoderWorker()?.postMessage({ type: 'clear_all' });
  const finalSave = dirty.value ? flushSave(false) : Promise.resolve();
  void finalSave.finally(() => {
    if (!lockedBy.value) {
      void releaseLabelingLock(projectUuid);
    }
  });
});

function handleBeforeUnload(e: BeforeUnloadEvent) {
  // The lock expires server-side; the labels must not be lost.
  if (dirty.value) {
    void flushSave(true);
    // Ask the browser for the unsaved-changes prompt: the keepalive save is
    // best-effort and capped at 64 KiB.
    e.preventDefault();
    e.returnValue = '';
  }
}

// pagehide, not beforeunload: it fires only when the page actually goes away
// — beforeunload also fires when the user cancels the unsaved-changes prompt
// and stays, which must not drop the lock under an active session.
function handlePageHide(e: PageTransitionEvent) {
  // Into the back/forward cache: the page may come back — keep the lock and
  // let pageshow's re-acquire (or the TTL) sort it out.
  if (e.persisted) {
    return;
  }
  // Closing the window frees colleagues like clicking the X does; the TTL
  // stays the net for crashes, where nothing runs at all. Only when no save
  // is pending OR in flight: the annotations PUT is lock-guarded, and
  // beforeunload's keepalive save clears `dirty` synchronously, so `saving`
  // is what actually witnesses the in-flight request.
  if (
    !dirty.value &&
    !saving.value &&
    !lockedBy.value &&
    !releasedWhileHidden
  ) {
    // cachedAuthToken is primed at mount and refreshed by every heartbeat;
    // in the worst case (null) the DELETE 401s and the TTL frees the lock.
    void releaseLabelingLock(projectUuid, {
      keepalive: true,
      authToken: cachedAuthToken,
    });
  }
}

// bfcache restore: the timers were frozen, the lock may have expired or been
// taken — re-acquire explicitly instead of waiting for the next heartbeat.
function handlePageShow(e: PageTransitionEvent) {
  if (e.persisted) {
    void retryLock();
  }
}

async function heartbeat() {
  cachedAuthToken = (await getAccessToken()) ?? null;
  try {
    await acquireLabelingLock(projectUuid);
  } catch (error) {
    if (error instanceof LabelingLockHeldError) {
      // Lock lost (throttled background tab, someone took over): the
      // blocking overlay stops further edits. No rescue save — the
      // annotations PUT is lock-guarded (409 without it); the unsaved edits
      // stay in memory and are flushed after a successful re-acquire.
      lockedBy.value = error.message;
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    }
  }
}

let hiddenReleaseTimer: ReturnType<typeof setTimeout> | null = null;
let releasedWhileHidden = false;

function handleVisibilityChange() {
  if (document.hidden) {
    if (!lockedBy.value && !releasedWhileHidden && !hiddenReleaseTimer) {
      hiddenReleaseTimer = setTimeout(
        () => void releaseWhileHidden(),
        HIDDEN_RELEASE_MS,
      );
    }
    return;
  }
  if (hiddenReleaseTimer) {
    clearTimeout(hiddenReleaseTimer);
    hiddenReleaseTimer = null;
  }
  if (releasedWhileHidden) {
    releasedWhileHidden = false;
    // Seamless when the lock is still free; the "locked by X" overlay when a
    // colleague took it in the meantime. Editing stays blocked until the
    // re-acquire settles — a save in that window would 409.
    void retryLock().finally(() => {
      reacquiringLock.value = false;
    });
  }
}

async function releaseWhileHidden() {
  hiddenReleaseTimer = null;
  if (lockedBy.value) {
    return;
  }
  releasedWhileHidden = true;
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  // Save strictly BEFORE releasing (the annotations PUT is lock-guarded) and
  // before raising reacquiringLock, which gates flushSave.
  await flushSave();
  // The user may have returned during the save — handleVisibilityChange then
  // cleared the flag and re-acquired. Releasing now would delete the lock
  // the client believes it holds (and raising reacquiringLock would re-lock
  // an editor retryLock already opened up).
  if (!releasedWhileHidden) {
    return;
  }
  reacquiringLock.value = true;
  void releaseLabelingLock(projectUuid);
}

async function retryLock() {
  try {
    await acquireLabelingLock(projectUuid);
    lockedBy.value = null;
    // Before the flush below — reacquiringLock gates flushSave.
    reacquiringLock.value = false;
    // pageshow may retry while an interval still runs — never stack two.
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
    }
    heartbeatTimer = setInterval(heartbeat, LOCK_HEARTBEAT_MS);
    // Edits made before the lock was lost are still in memory — push them
    // now that saving can succeed again.
    void flushSave();
    if (images.value.length === 0) {
      loading.value = true;
      void initializeWorker();
      await loadClasses();
      await loadImages();
      loading.value = false;
    }
  } catch (error) {
    if (error instanceof LabelingLockHeldError) {
      lockedBy.value = error.message;
    } else {
      // A network failure must not leave the editor unlocked-but-editable
      // with no heartbeat: show the overlay so Retry stays one click away.
      console.error('Re-acquiring the labeling lock failed:', error);
      lockedBy.value = $gettext(
        'The labeling lock could not be re-acquired — check the connection and retry.',
      );
    }
  }
}

async function loadClasses() {
  // The backend owns the class list (the synthetic model's classes) — no
  // client-side fallback, or the class lists could silently differ from
  // what the dataset build validates against.
  try {
    projectClasses.value = await getProjectClasses(projectUuid);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    projectClasses.value = [];
    alertsStore.error(error.message);
  }
}

async function loadImages() {
  images.value = await listLabelingImages(projectUuid);
  if (images.value.length === 0) {
    return;
  }
  const requested = props.initialImageUuid;
  let index = requested
    ? images.value.findIndex((image) => image.uuid === requested)
    : -1;
  if (index === -1) {
    // First image that still needs work, or the first one.
    index = images.value.findIndex(
      (image) => image.status !== LabelingImageStatus.Ready,
    );
    if (index === -1) {
      index = 0;
    }
  }
  await openImage(index);
}

// ---- image session ----

// Increments per openImage call: a slower earlier open (held ArrowRight,
// flaky presigned fetches) must not clobber the session of a later one.
let openToken = 0;

async function openImage(index: number) {
  if (index < 0 || index >= images.value.length) {
    return;
  }
  const token = ++openToken;
  if (dirty.value) {
    await flushSave();
    if (token !== openToken) {
      return;
    }
    if (dirty.value) {
      // The save failed — switching now would replace the session state and
      // silently drop the edits. Stay on the unsaved image.
      alertsStore.error(
        $gettext(
          'Could not save this image — check the connection and try again.',
        ),
      );
      return;
    }
  }
  if (token !== openToken) {
    return;
  }
  stopEmbeddingPoll();
  canvas.value?.clearSamPoints();
  // Embeddings are re-sent per open; without this every visited image
  // parks ~4 MB in the worker's map for the whole session.
  samDecoderWorker()?.postMessage({ type: 'clear_all' });
  currentIndex.value = index;
  undoStack = [];
  samReady.value = false;
  samPending.value = false;
  samHintShown = false;
  instances.value = [];
  selectedId.value = null;
  prelabelInfo.value = null;
  imageElement.value = null;

  const image = images.value[index];

  try {
    const files = await getLabelingImageFiles(projectUuid, image.uuid);

    const element = new Image();
    element.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      element.onload = () => resolve();
      element.onerror = () =>
        reject(new Error($gettext('Could not load the image')));
      element.src = files.imageUrl;
    });

    let prelabels: unknown = null;
    try {
      prelabels = await fetchPresignedJson(files.infoUrl);
    } catch {
      prelabels = null;
    }

    let loaded: EditorInstance[] = [];
    if (files.annotationsUrl) {
      try {
        const doc = await fetchPresignedJson<PerImageCocoDoc>(
          files.annotationsUrl,
        );
        loaded = cocoDocToInstances(doc, element.width, element.height);
      } catch (error) {
        console.error('Failed to load saved annotations:', error);
      }
    }
    if (token !== openToken) {
      return;
    }

    prelabelInfo.value = prelabels;
    if (loaded.length === 0 && prelabels) {
      loaded = prelabelsToInstances(
        prelabels,
        projectClasses.value,
        element.width,
        element.height,
      );
    }
    nextInstanceId = loaded.length;
    // Fresh images start with an empty object list — the first edit with
    // any tool creates an object on the fly (ensureInstanceForEdit).
    instances.value = loaded;
    selectedId.value = instances.value[0]?.id ?? null;
    imageElement.value = element;
    dirty.value = false;
    saveFailed.value = false;
    await nextTick();
    if (token !== openToken) {
      return;
    }
    canvas.value?.markAllDirty();
    canvas.value?.zoomToFit();
    scheduleCutout();
    void loadEmbedding(image);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (token === openToken) {
      alertsStore.error(error.message ?? $gettext('Could not load the image'));
    }
  }
}

// ---- rotation-card cutout ----

const cutoutUrl = ref<string | null>(null);
let cutoutTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleCutout() {
  if (cutoutTimer) {
    clearTimeout(cutoutTimer);
  }
  cutoutTimer = setTimeout(updateCutout, 300);
}

/** Crop the selected instance's bbox from the image, keep only mask pixels,
 * and render it as the rotation card's thumbnail (per design: "measured on
 * the part cutout"). */
function updateCutout() {
  const instance = selectedInstance.value;
  const image = imageElement.value;
  if (!instance || !image) {
    cutoutUrl.value = null;
    return;
  }
  const bbox = maskBBox(instance.mask, image.width, image.height);
  if (!bbox) {
    cutoutUrl.value = null;
    return;
  }
  const [bx, by, bw, bh] = bbox;
  const scale = Math.min(1, 96 / Math.max(bw, bh));
  const cw = Math.max(1, Math.round(bw * scale));
  const ch = Math.max(1, Math.round(bh * scale));
  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const ctx = out.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.drawImage(image, bx, by, bw, bh, 0, 0, cw, ch);
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = bw;
  maskCanvas.height = bh;
  const maskCtx = maskCanvas.getContext('2d');
  if (maskCtx) {
    const alpha = maskCtx.createImageData(bw, bh);
    for (let y = 0; y < bh; y++) {
      for (let x = 0; x < bw; x++) {
        if (instance.mask[(by + y) * image.width + bx + x] > 0) {
          alpha.data[(y * bw + x) * 4 + 3] = 255;
        }
      }
    }
    maskCtx.putImageData(alpha, 0, 0);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskCanvas, 0, 0, bw, bh, 0, 0, cw, ch);
    ctx.globalCompositeOperation = 'source-over';
  }
  cutoutUrl.value = out.toDataURL();
}

watch(selectedId, scheduleCutout);

// ---- SAM assist ----

// Resolves true once the shared decoder worker is ready. loadEmbedding
// awaits it, so the first image doesn't lose SAM to the init race. The
// worker itself is app-wide (see SamDecoderSession) — usually pre-warmed by
// the Labeling tab, so opening the editor attaches instantly.
let workerReady: Promise<boolean> | null = null;
let detachWorkerListener: (() => void) | null = null;

function handleWorkerMessage(e: MessageEvent) {
  const { type, data } = e.data;
  if (type === 'embeddings_ready') {
    if (data.imageKey === currentImage.value?.uuid) {
      samReady.value = true;
      samPending.value = false;
    }
    return;
  }
  if (type === 'mask_ready') {
    if (data.preview) {
      handlePreviewReady(data);
      return;
    }
    decoding.value = false;
    if (data.imageKey !== currentImage.value?.uuid) {
      return;
    }
    const instance = selectedInstance.value;
    const element = imageElement.value;
    if (!instance || !element) {
      return;
    }
    if (data.mask.data.length !== element.width * element.height) {
      // Server-side meta disagrees with the loaded image — refuse the
      // silently misaligned mask.
      console.error(
        `SAM mask size ${data.mask.width}×${data.mask.height} does not match the image.`,
      );
      return;
    }
    pushUndoSnapshot();
    instance.mask = markRaw(new Uint8Array(data.mask.data));
    instance.hasMask = maskHasPixels(instance.mask);
    ensureKeypoint(instance, element.width, element.height);
    instance.touched = true;
    if (instance.source === 'prelabel') {
      instance.source = 'human';
    }
    // The committed mask replaces the hover preview.
    canvas.value?.setPreviewMask(null);
    canvas.value?.markDirty(instance.id);
    canvas.value?.redraw();
    markEdited();
    return;
  }
  if (type === 'error') {
    if (data.preview) {
      previewInFlight = false;
      queuedPreview = null;
      return;
    }
    decoding.value = false;
    console.error('SAM decoder error:', data.message);
  }
}

// ---- hover preview (decode-on-hover in AI mode) ----
// A decode takes ~300 ms on the CPU, so at most one preview is in flight;
// the newest hover position waits in a one-slot queue (older ones drop).
let previewInFlight = false;
let queuedPreview: PromptPoint[] | null = null;

function requestPreview(points: PromptPoint[]) {
  // Never let previews queue behind (or delay) a real click decode.
  if (!samReady.value || decoding.value || !currentImage.value) {
    return;
  }
  if (previewInFlight) {
    queuedPreview = points;
    return;
  }
  const decoder = samDecoderWorker();
  if (!decoder) {
    return;
  }
  previewInFlight = true;
  decoder.postMessage({
    type: 'create_mask',
    imageKey: currentImage.value.uuid,
    preview: true,
    data: points,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handlePreviewReady(data: any) {
  previewInFlight = false;
  if (queuedPreview) {
    const next = queuedPreview;
    queuedPreview = null;
    requestPreview(next);
  }
  const element = imageElement.value;
  if (
    data.imageKey !== currentImage.value?.uuid ||
    !element ||
    data.mask.data.length !== element.width * element.height ||
    tool.value !== 'sam'
  ) {
    return;
  }
  canvas.value?.setPreviewMask(data.mask.data);
}

function initializeWorker() {
  if (workerReady) {
    return;
  }
  samModelLoading.value = true;
  workerReady = warmSamDecoder();
  void workerReady.then((ok) => {
    samModelLoading.value = false;
    samUnavailable.value = !ok;
    const sharedWorker = samDecoderWorker();
    if (ok && sharedWorker) {
      sharedWorker.addEventListener('message', handleWorkerMessage);
      detachWorkerListener = () =>
        sharedWorker.removeEventListener('message', handleWorkerMessage);
    }
  });
}

// A disabled tool must never stay active — if SAM is selected but becomes
// (or turns out to be) unavailable, fall back to the brush.
watch(
  () => samReady.value || tool.value !== 'sam',
  (usable) => {
    if (!usable) {
      tool.value = 'brush';
    }
  },
  { immediate: true },
);

async function loadEmbedding(image: LabelingImage) {
  // Pending state first so the "computing" chip shows while the worker
  // itself is still initializing.
  samPending.value = true;
  if (!(await (workerReady ?? Promise.resolve(false)))) {
    samPending.value = false;
    return;
  }
  const decoder = samDecoderWorker();
  if (!decoder || image.uuid !== currentImage.value?.uuid) {
    return;
  }
  try {
    const embedding = await getImageEmbedding(projectUuid, image.uuid);
    if (image.uuid !== currentImage.value?.uuid) {
      return;
    }
    if (!embedding) {
      embeddingPollTimer = setTimeout(
        () => void loadEmbedding(image),
        EMBEDDING_POLL_MS,
      );
      return;
    }
    const element = imageElement.value;
    if (!element) {
      return;
    }
    const buffer = await fetchPresignedBinary(embedding.url);
    let meta: SamEmbeddingMeta | null = null;
    if (embedding.metaUrl) {
      try {
        meta = await fetchPresignedJson<SamEmbeddingMeta>(embedding.metaUrl);
      } catch {
        meta = null;
      }
    }
    // Without the sidecar meta the sizes follow SAM's fixed preprocessing:
    // the longest side is scaled to 1024.
    const scale = 1024 / Math.max(element.width, element.height);
    const originalSize = meta?.original_size ?? [element.height, element.width];
    const reshapedInputSize = meta?.reshaped_input_size ?? [
      Math.round(element.height * scale),
      Math.round(element.width * scale),
    ];
    decoder.postMessage(
      {
        type: 'set_embeddings',
        imageKey: image.uuid,
        data: { embedding: buffer, originalSize, reshapedInputSize },
      },
      [buffer],
    );
  } catch (error) {
    console.error('Failed to load the SAM embedding:', error);
    samPending.value = false;
  }
}

function stopEmbeddingPoll() {
  if (embeddingPollTimer) {
    clearTimeout(embeddingPollTimer);
    embeddingPollTimer = null;
  }
}

// One hint per image: repeated clicks on a not-yet-ready SAM tool shouldn't
// stack snackbars.
let samHintShown = false;

function onSamNotReady() {
  if (samHintShown) {
    return;
  }
  samHintShown = true;
  alertsStore.info(
    samUnavailable.value
      ? $gettext('AI Segmentation is currently unavailable.')
      : $gettext(
          'AI Segmentation for this image is still being prepared — try again in a moment.',
        ),
  );
}

function requestMask(points: PromptPoint[]) {
  const decoder = samDecoderWorker();
  if (!decoder || !currentImage.value) {
    return;
  }
  decoding.value = true;
  decoder.postMessage({
    type: 'create_mask',
    imageKey: currentImage.value.uuid,
    data: points,
  });
}

// ---- editing ----

function cloneInstancesShallow(source: EditorInstance[]): EditorInstance[] {
  // Mask buffers are shared with the live instances — pushUndoSnapshot
  // detaches the one that is about to be mutated.
  return source.map((instance) => ({
    ...instance,
    keypoint: instance.keypoint ? [...instance.keypoint] : null,
  }));
}

/**
 * Undo snapshot with copy-on-write masks: instead of deep-copying every mask
 * (≈40 MB per snapshot on a 2 MP image with 20 objects, ×10 stack depth),
 * the snapshot shares all mask buffers and only the instance whose mask the
 * caller is about to mutate IN PLACE (`mutatingId` — brush/erase strokes,
 * polygon commits) is detached: history keeps the old buffer, the live
 * instance gets a fresh copy to draw on. Buffer-replacing edits (SAM sets a
 * new mask) and structural/field edits never touch shared buffers, so they
 * pass no id. Invariant: a buffer referenced by the undo stack is never
 * mutated afterwards.
 */
function pushUndoSnapshot(mutatingId?: number | null) {
  undoStack.push({
    instances: cloneInstancesShallow(instances.value),
    selectedId: selectedId.value,
  });
  if (undoStack.length > UNDO_LIMIT) {
    undoStack.shift();
  }
  if (mutatingId != null) {
    const live = instances.value.find((instance) => instance.id === mutatingId);
    if (live) {
      live.mask = markRaw(new Uint8Array(live.mask));
    }
  }
}

async function undo() {
  const snapshot = undoStack.pop();
  if (!snapshot) {
    return;
  }
  instances.value = snapshot.instances;
  selectedId.value = snapshot.selectedId;
  // The canvas sees the restored instances only after the props flush;
  // rebuilding overlays before that would cache the pre-undo masks as
  // clean and the revert never shows.
  await nextTick();
  canvas.value?.markAllDirty();
  canvas.value?.redraw();
  markEdited();
}

async function resetToPrelabel() {
  if (!imageElement.value) {
    return;
  }
  pushUndoSnapshot();
  instances.value = prelabelInfo.value
    ? prelabelsToInstances(
        prelabelInfo.value,
        projectClasses.value,
        imageElement.value.width,
        imageElement.value.height,
      )
    : [];
  nextInstanceId = instances.value.length;
  selectedId.value = instances.value[0]?.id ?? null;
  canvas.value?.clearSamPoints();
  // See undo: overlays rebuild only after the props flush.
  await nextTick();
  canvas.value?.markAllDirty();
  canvas.value?.redraw();
  markEdited();
}

function createEmptyInstance(width: number, height: number): EditorInstance {
  return {
    id: nextInstanceId++,
    categoryName: projectClasses.value[0] ?? '',
    mask: markRaw(new Uint8Array(width * height)),
    keypoint: null,
    angleDeg: rotationEnabled.value ? 0 : null,
    source: 'human',
    touched: true,
    hasMask: false,
  };
}

function addInstance() {
  if (!imageElement.value) {
    return;
  }
  pushUndoSnapshot();
  const instance = createEmptyInstance(
    imageElement.value.width,
    imageElement.value.height,
  );
  instances.value.push(instance);
  selectedId.value = instance.id;
  if (tool.value === 'pan') {
    tool.value = 'sam';
  }
  markEdited();
}

/** "Object Done": deselect and arm the last-used mask tool. The next canvas
 * click starts the next object via lazy creation — so finishing the final
 * object never leaves an empty stray behind. */
function finishObject() {
  if (!canFinishObject.value) {
    return;
  }
  selectedId.value = null;
  // SAM may have become unavailable since it was last used — arming it would
  // just bounce to the fallback tool with a visible rail flicker.
  tool.value =
    lastMaskTool.value === 'sam' && !samReady.value
      ? 'brush'
      : lastMaskTool.value;
}

function removeInstance(id: number) {
  pushUndoSnapshot();
  // An empty list is a valid state — the next edit with any tool creates a
  // fresh object via ensureInstanceForEdit.
  instances.value = instances.value.filter((instance) => instance.id !== id);
  if (selectedId.value === id) {
    selectedId.value = instances.value[0]?.id ?? null;
  }
  canvas.value?.redraw();
  markEdited();
}

/** Target for the canvas tools: the selected object, or — with none left —
 * a fresh empty object created on the fly, so drawing always just works. */
function ensureInstanceForEdit(): EditorInstance | null {
  if (selectedInstance.value) {
    return selectedInstance.value;
  }
  if (!imageElement.value || isDone.value) {
    return null;
  }
  const instance = createEmptyInstance(
    imageElement.value.width,
    imageElement.value.height,
  );
  instances.value.push(instance);
  selectedId.value = instance.id;
  return instance;
}

function selectInstance(id: number) {
  selectedId.value = id;
}

function setClass(name: string) {
  const instance = selectedInstance.value;
  if (!instance) {
    return;
  }
  instance.categoryName = name;
  instance.touched = true;
  markEdited();
}

function setAngle(deg: number) {
  const instance = selectedInstance.value;
  if (!instance) {
    return;
  }
  // Adjusting the angle in the Details panel is rotation work — activate
  // the rotation tool so drag and arrow keys continue it on the canvas.
  if (rotationEnabled.value && !isDone.value) {
    tool.value = 'rotate';
  }
  // One undo snapshot per slider gesture, burst-batched like the arrow keys
  // (a drag emits a value per pixel — snapshotting each would flush the
  // whole undo stack).
  const now = Date.now();
  if (now - lastNudgeAt > 1000) {
    pushUndoSnapshot();
  }
  lastNudgeAt = now;
  instance.angleDeg = deg;
  instance.touched = true;
  canvas.value?.redraw();
  markEdited();
}

function setKeypoint(point: [number, number]) {
  const instance = selectedInstance.value;
  if (!instance) {
    return;
  }
  pushUndoSnapshot();
  instance.keypoint = point;
  instance.touched = true;
  canvas.value?.redraw();
  markEdited();
}

async function toggleRotation() {
  rotationEnabled.value = !rotationEnabled.value;
  if (!rotationEnabled.value && tool.value === 'rotate') {
    // The rotation tool just disappeared from the rail.
    tool.value = 'pan';
  }
  canvas.value?.redraw();
  try {
    await setRotationLabeling(projectUuid, rotationEnabled.value);
    if (project.value) {
      project.value.rotation_labeling = rotationEnabled.value;
    }
  } catch (error) {
    // Revert the optimistic flip — a switch that shows a state the backend
    // never stored would silently diverge on the next session.
    rotationEnabled.value = !rotationEnabled.value;
    canvas.value?.redraw();
    console.error('Failed to store the rotation setting:', error);
    alertsStore.error($gettext('Could not save the rotation setting.'));
  }
}

async function chooseRotation(enabled: boolean) {
  rotationDialog.value = false;
  rotationEnabled.value = enabled;
  try {
    await setRotationLabeling(projectUuid, enabled);
    if (project.value) {
      project.value.rotation_labeling = enabled;
    }
  } catch (error) {
    console.error('Failed to store the rotation setting:', error);
  }
}

// ---- persistence ----

function markEdited() {
  // Ready images are read-only until "Reopen for editing" — nothing that
  // slips past the disabled controls may autosave or change the status.
  // Same while the lock is being re-acquired: the save would 409.
  if (isDone.value || reacquiringLock.value) {
    return;
  }
  dirty.value = true;
  // A fresh edit is a fresh reason to try — restart the backoff ladder.
  saveRetries = 0;
  scheduleCutout();
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
  }
  autosaveTimer = setTimeout(() => void flushSave(), AUTOSAVE_DEBOUNCE_MS);
}

function buildDoc(image: LabelingImage) {
  if (!imageElement.value) {
    return null;
  }
  return instancesToCocoDoc(
    instances.value,
    `${image.uuid}.color.jpg`,
    imageElement.value.width,
    imageElement.value.height,
    rotationEnabled.value,
  );
}

/**
 * Serialize and PUT the current image's annotations. Returns whether the
 * save succeeded. `dirty` is cleared before the await so edits made while
 * the request is in flight re-set the flag and the next autosave picks
 * them up instead of being swallowed by a post-await reset.
 */
async function saveAnnotations(options: {
  markReady?: boolean;
  keepalive?: boolean;
}): Promise<boolean> {
  const image = currentImage.value;
  if (!image) {
    return false;
  }
  const doc = buildDoc(image);
  if (!doc) {
    return false;
  }
  const savedIndex = currentIndex.value;
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
    autosaveTimer = null;
  }
  saving.value = true;
  dirty.value = false;
  try {
    const updated = await putImageAnnotations(projectUuid, image.uuid, doc, {
      ...options,
      authToken: cachedAuthToken,
    });
    saveFailed.value = false;
    lastSavedAt.value = new Date();
    if (updated) {
      images.value[savedIndex] = { ...updated, imageUrl: image.imageUrl };
    }
    return true;
  } catch (error) {
    console.error('Saving annotations failed:', error);
    dirty.value = true;
    saveFailed.value = true;
    if (error instanceof LabelingLockHeldError) {
      // 409 = the lock is gone — surface the blocking overlay instead of
      // retrying into the same rejection; the edits stay in memory and are
      // flushed after a successful re-acquire.
      lockedBy.value = error.message;
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    }
    return false;
  } finally {
    saving.value = false;
  }
}

// Failed saves retry with capped exponential backoff — a flat retry-forever
// hammers a dead endpoint every 3 s without ever surfacing the problem.
// Any successful save (or fresh user edit via markEdited) resets the clock.
const MAX_SAVE_RETRIES = 5;
let saveRetries = 0;

async function flushSave(keepalive = false) {
  if (!dirty.value) {
    return;
  }
  // A pending timer may fire after the lock was lost or released — the PUT
  // would only 409. The edits stay dirty in memory and are flushed after a
  // successful re-acquire (retryLock).
  if (lockedBy.value || reacquiringLock.value) {
    return;
  }
  const saved = await saveAnnotations({ keepalive });
  if (saved) {
    saveRetries = 0;
    return;
  }
  if (keepalive || lockedBy.value || saveRetries >= MAX_SAVE_RETRIES) {
    // Give up on automatic retries; `dirty` stays set, so the toolbar keeps
    // showing the failure and the next edit or navigation retries.
    return;
  }
  saveRetries++;
  if (autosaveTimer) {
    clearTimeout(autosaveTimer);
  }
  autosaveTimer = setTimeout(
    () => void flushSave(),
    AUTOSAVE_DEBOUNCE_MS * 2 ** saveRetries,
  );
}

async function markAsLabeled() {
  if (!currentImage.value || doneHint.value) {
    return;
  }
  // Save + ready-mark in one atomic request.
  if (!(await saveAnnotations({ markReady: true }))) {
    alertsStore.error($gettext('Could not save the annotations'));
    return;
  }
  const nextIndex = images.value.findIndex(
    (candidate, index) =>
      index > currentIndex.value &&
      candidate.status !== LabelingImageStatus.Ready,
  );
  if (nextIndex !== -1) {
    await openImage(nextIndex);
  } else {
    alertsStore.success($gettext('All images are labeled'));
  }
}

async function reopenForEditing() {
  const image = currentImage.value;
  if (!image) {
    return;
  }
  try {
    const updated = await patchLabelingImage(
      projectUuid,
      image.uuid,
      LabelingImageStatus.InProgress,
    );
    images.value[currentIndex.value] = { ...updated, imageUrl: image.imageUrl };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    alertsStore.error(error.message);
  }
}

// ---- navigation ----

function previousImage() {
  if (currentIndex.value > 0) {
    void openImage(currentIndex.value - 1);
  }
}

function nextImage() {
  if (currentIndex.value < images.value.length - 1) {
    void openImage(currentIndex.value + 1);
  }
}

function close() {
  emit('close');
}

function handleKeydown(e: KeyboardEvent) {
  const target = e.target as HTMLElement | null;
  if (
    target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      // Vuetify slider thumbs are focusable divs that handle arrow keys
      // themselves (rotation/brush sliders) — handling them here too would
      // make every press count twice.
      target.getAttribute('role') === 'slider')
  ) {
    return;
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
    e.preventDefault();
    undo();
    return;
  }
  // Enter = "Object Done". Not while a button has focus (Enter activates
  // it), a polygon draft is open (too easy to fire meaning "close the
  // polygon"), or a blocking dialog/overlay is up.
  if (
    e.key === 'Enter' &&
    canFinishObject.value &&
    polygonCount.value === 0 &&
    !rotationDialog.value &&
    !lockedBy.value &&
    !target?.closest('button')
  ) {
    e.preventDefault();
    finishObject();
    return;
  }
  if (e.key === 'Escape') {
    // First Escape abandons a half-done polygon draft / SAM prompt points;
    // with nothing in progress it drops back to the pan tool.
    const hadDraft = canvas.value?.cancelDrafts();
    if (!hadDraft) {
      tool.value = 'pan';
      // The last-clicked tool button keeps its focus ring — with the pan
      // button now active, two tools would look selected at once.
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    return;
  }

  const toolKeys: Record<string, LabelingToolId> = {
    s: 'sam',
    b: 'brush',
    e: 'erase',
    p: 'polygon',
    k: 'keypoint',
    r: 'rotate',
    h: 'pan',
  };
  const mapped = toolKeys[e.key.toLowerCase()];
  if (mapped === 'rotate' && !rotationEnabled.value) {
    return;
  }
  if (mapped) {
    // Ready images are read-only; navigation keys below stay usable.
    if (!isDone.value) {
      tool.value = mapped;
      // Same focus-ring cleanup as Escape above.
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    return;
  }
  // With the pick-point tool active, arrows nudge the keypoint (Shift =
  // 10 px); otherwise left/right navigate between images.
  const nudges: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  };
  const nudge = nudges[e.key];
  if (
    nudge &&
    tool.value === 'keypoint' &&
    !isDone.value &&
    selectedInstance.value?.keypoint &&
    imageElement.value
  ) {
    e.preventDefault();
    nudgeKeypoint(nudge, e.shiftKey ? 10 : 1);
    return;
  }
  // With the rotation tool active, left/right turn the selected object's
  // arrow (Shift = 10°) instead of switching images.
  if (
    (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
    tool.value === 'rotate' &&
    !isDone.value &&
    selectedInstance.value
  ) {
    e.preventDefault();
    rotateSelected(e.key === 'ArrowLeft' ? -1 : 1, e.shiftKey ? 10 : 1);
    return;
  }
  if (e.key === 'ArrowLeft') {
    previousImage();
  } else if (e.key === 'ArrowRight') {
    nextImage();
  }
}

// One undo snapshot per nudge burst, not per keystroke — arrow-key repeats
// would flush the whole undo stack otherwise.
let lastNudgeAt = 0;

function nudgeKeypoint(direction: [number, number], step: number) {
  const instance = selectedInstance.value;
  const element = imageElement.value;
  if (!instance?.keypoint || !element) {
    return;
  }
  const now = Date.now();
  if (now - lastNudgeAt > 1000) {
    pushUndoSnapshot();
  }
  lastNudgeAt = now;
  instance.keypoint = [
    Math.min(
      Math.max(instance.keypoint[0] + direction[0] * step, 0),
      element.width - 1,
    ),
    Math.min(
      Math.max(instance.keypoint[1] + direction[1] * step, 0),
      element.height - 1,
    ),
  ];
  instance.touched = true;
  canvas.value?.redraw();
  markEdited();
}

function rotateSelected(direction: -1 | 1, step: number) {
  const instance = selectedInstance.value;
  if (!instance) {
    return;
  }
  // Same burst-batched undo as keypoint nudges.
  const now = Date.now();
  if (now - lastNudgeAt > 1000) {
    pushUndoSnapshot();
  }
  lastNudgeAt = now;
  const next = (instance.angleDeg ?? 0) + direction * step;
  // Wrap into the stored -180..180 convention.
  instance.angleDeg = ((((next + 180) % 360) + 360) % 360) - 180;
  instance.touched = true;
  canvas.value?.redraw();
  markEdited();
}

const statusColor = (status: LabelingImageStatus | undefined) =>
  status === LabelingImageStatus.Ready
    ? 'success'
    : status === LabelingImageStatus.InProgress
      ? 'warning'
      : 'grey';
</script>

<template>
  <v-card rounded="xl" class="labeling-editor d-flex flex-column">
    <!-- top bar -->
    <v-toolbar density="comfortable" flat border class="flex-grow-0 px-2">
      <v-icon
        :color="statusColor(currentImage?.status)"
        icon="mdi-square-rounded"
        size="x-small"
        class="ml-2 mr-2"
      />
      <span class="text-body-2 font-weight-medium text-truncate">
        {{ currentImage?.sourceName ?? $gettext('Labeling') }}
      </span>
      <span class="text-caption text-medium-emphasis ml-2">
        {{ resolutionText }}
      </span>
      <span
        v-if="savedText"
        class="text-caption ml-4 d-inline-flex align-center"
        :class="saveFailed ? 'text-error' : 'text-medium-emphasis'"
      >
        <v-icon
          :icon="
            saveFailed ? 'mdi-cloud-alert-outline' : 'mdi-cloud-check-outline'
          "
          size="small"
          class="mr-1"
        />
        {{ savedText }}
      </span>
      <!-- Short and model-agnostic, also so it cannot run into the centered
           pager. -->
      <v-chip
        v-if="(samModelLoading || samPending) && !samReady && !samUnavailable"
        size="small"
        color="warning"
        density="comfortable"
        class="ml-4 flex-shrink-0"
      >
        <v-progress-circular indeterminate size="12" width="2" class="mr-2" />
        {{ $gettext('Preparing AI Segmentation…') }}
      </v-chip>
      <v-spacer />
      <!-- Absolutely centered: flow layout would shift the pager whenever
           the SAM chip appears or disappears on the left. -->
      <div class="d-flex align-center pager-pill pager-centered px-1">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          size="small"
          :disabled="currentIndex === 0"
          :aria-label="$gettext('Previous image')"
          @click="previousImage"
        />
        <span class="text-body-2 mx-1 position-text">{{ positionText }}</span>
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          size="small"
          :disabled="currentIndex >= images.length - 1"
          :aria-label="$gettext('Next image')"
          @click="nextImage"
        />
      </div>
      <template v-if="isDone">
        <v-chip
          color="success"
          density="comfortable"
          class="mr-3 lock-chip"
          prepend-icon="mdi-lock"
        >
          {{ $gettext('Labeled — locked') }}
        </v-chip>
        <r-btn
          variant="outlined"
          icon="mdi-lock-open-variant-outline"
          class="mr-2"
          @click="reopenForEditing"
        >
          {{ $gettext('Unlock for Editing') }}
        </r-btn>
      </template>
      <template v-else>
        <r-btn
          variant="outlined"
          class="mr-2"
          :disabled="currentIndex >= images.length - 1"
          @click="nextImage"
        >
          {{ $gettext('Skip') }}
        </r-btn>
        <v-tooltip location="bottom" :disabled="!doneHint && !emptyHint">
          <template v-slot:activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <r-btn
                variant="accent"
                icon="mdi-check-bold"
                :disabled="!!doneHint || !currentImage"
                @click="markAsLabeled"
              >
                {{ $gettext('Mark as Labeled') }}
              </r-btn>
            </span>
          </template>
          <span>{{ doneHint ?? emptyHint }}</span>
        </v-tooltip>
      </template>
      <v-tooltip location="bottom" :text="$gettext('Open Documentation')">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn
            v-bind="tooltipProps"
            icon="mdi-help-circle-outline"
            variant="text"
            class="ml-2"
            :aria-label="$gettext('Open Documentation')"
            @click="openRobDocs(ROBDOCS_LABELING_URL)"
          />
        </template>
      </v-tooltip>
      <v-btn
        icon="mdi-close"
        variant="text"
        :aria-label="$gettext('Close labeling tool')"
        @click="close"
      />
    </v-toolbar>

    <!-- body -->
    <div class="d-flex flex-grow-1 editor-body">
      <objects-panel
        v-if="!panelsCollapsed"
        :instances="instances"
        :selected-id="selectedId"
        :ready-count="readyCount"
        :total-count="images.length"
        :read-only="isDone || reacquiringLock"
        :rotation-enabled="rotationEnabled"
        @select="selectInstance"
        @remove="removeInstance"
        @add="addInstance"
      />
      <details-panel
        v-if="!panelsCollapsed"
        :instance="selectedInstance"
        :instance-number="selectedNumber"
        :categories="projectClasses"
        :rotation-enabled="rotationEnabled"
        :done-hint="doneHint"
        :read-only="isDone || reacquiringLock"
        :cutout-url="cutoutUrl"
        @set-class="setClass"
        @set-angle="setAngle"
        @toggle-rotation="toggleRotation"
      />
      <div class="canvas-area flex-grow-1">
        <annotation-canvas
          ref="canvas"
          :image-element="imageElement"
          :instances="instances"
          :selected-id="selectedId"
          :tool="tool"
          :prompt-mode="promptMode"
          :brush-size="brushSize"
          :rotation-enabled="rotationEnabled"
          :sam-ready="samReady"
          :read-only="isDone || reacquiringLock"
          :ensure-instance="ensureInstanceForEdit"
          @before-edit="() => pushUndoSnapshot(selectedId)"
          @edited="markEdited"
          @request-mask="requestMask"
          @request-preview="requestPreview"
          @sam-not-ready="onSamNotReady"
          @keypoint-set="setKeypoint"
          @select-instance="selectInstance"
          @zoom-changed="(z: number) => (zoomPct = Math.round(z * 100))"
          @polygon-count-changed="(count: number) => (polygonCount = count)"
        />

        <!-- panel collapse toggle — labeling on half a screen needs the
             canvas more than the side panels. -->
        <v-tooltip
          location="end"
          :text="
            panelsCollapsed ? $gettext('Show panels') : $gettext('Hide panels')
          "
        >
          <template v-slot:activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :icon="
                panelsCollapsed
                  ? 'mdi-chevron-double-right'
                  : 'mdi-chevron-double-left'
              "
              size="small"
              variant="elevated"
              class="panel-toggle"
              :aria-label="
                panelsCollapsed
                  ? $gettext('Show panels')
                  : $gettext('Hide panels')
              "
              @click="panelsCollapsed = !panelsCollapsed"
            />
          </template>
        </v-tooltip>

        <!-- tool rail — hidden while the image is locked (Ready): showing
             disabled tools would invite clicks; the Unlock button is the way
             back in. -->
        <v-sheet
          v-if="!isDone"
          class="tool-rail pa-1"
          rounded="lg"
          elevation="6"
        >
          <!-- Single-select toggle: correct active/focus state without the
               manual blur workaround a plain button list needed. -->
          <v-btn-toggle
            v-model="tool"
            mandatory
            density="compact"
            class="tool-toggle d-flex flex-column h-auto"
          >
            <v-tooltip
              v-for="toolDef in tools"
              :key="toolDef.id"
              location="start"
            >
              <template v-slot:activator="{ props: tooltipProps }">
                <!-- Disabled buttons swallow pointer events; the wrapper
                     keeps the explanatory tooltip hoverable. -->
                <span v-bind="tooltipProps">
                  <v-btn
                    :value="toolDef.id"
                    :icon="toolDef.icon"
                    size="small"
                    variant="text"
                    :disabled="toolDef.disabled"
                    :aria-label="toolDef.tip"
                  />
                </span>
              </template>
              <span>{{ toolDef.tip }}</span>
            </v-tooltip>
          </v-btn-toggle>
          <v-divider class="my-1" />
          <v-tooltip location="start">
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon="mdi-undo"
                size="small"
                variant="text"
                :aria-label="$gettext('Undo')"
                @click="undo"
              />
            </template>
            <span>{{ $gettext('Undo') }}</span>
          </v-tooltip>
          <v-tooltip location="start">
            <template v-slot:activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon="mdi-restart"
                size="small"
                variant="text"
                :aria-label="$gettext('Reset to prelabel')"
                @click="resetToPrelabel"
              />
            </template>
            <span>{{ $gettext('Reset to prelabel') }}</span>
          </v-tooltip>
        </v-sheet>

        <!-- zoom pill -->
        <v-sheet
          class="zoom-pill pa-1 d-flex align-center"
          rounded="pill"
          elevation="6"
        >
          <v-btn
            icon="mdi-minus"
            size="x-small"
            variant="text"
            :aria-label="$gettext('Zoom out')"
            @click="canvas?.zoomOut()"
          />
          <span class="text-caption zoom-value">{{ zoomPct }}%</span>
          <v-btn
            icon="mdi-plus"
            size="x-small"
            variant="text"
            :aria-label="$gettext('Zoom in')"
            @click="canvas?.zoomIn()"
          />
          <v-btn
            icon="mdi-fit-to-screen"
            size="x-small"
            variant="text"
            :aria-label="$gettext('Fit to window')"
            @click="canvas?.zoomToFit()"
          />
        </v-sheet>

        <!-- contextual tool options + finish-object action -->
        <div class="bottom-bar d-flex align-center ga-2">
          <v-sheet
            v-if="tool === 'sam'"
            class="tool-options pa-1 d-flex"
            rounded="pill"
            elevation="6"
          >
            <v-btn-toggle
              v-model="promptMode"
              density="compact"
              mandatory
              rounded="pill"
            >
              <v-btn
                value="positive"
                size="small"
                prepend-icon="mdi-plus-thick"
              >
                {{ $gettext('Positive') }}
              </v-btn>
              <v-btn
                value="negative"
                size="small"
                prepend-icon="mdi-minus-thick"
              >
                {{ $gettext('Negative') }}
              </v-btn>
            </v-btn-toggle>
            <v-progress-circular
              v-if="decoding"
              indeterminate
              size="20"
              width="2"
              class="ma-2"
            />
          </v-sheet>
          <v-sheet
            v-else-if="tool === 'brush' || tool === 'erase'"
            class="tool-options pa-2 px-4 d-flex align-center"
            rounded="pill"
            elevation="6"
          >
            <span class="text-caption mr-3">{{ $gettext('Brush') }}</span>
            <v-slider
              v-model="brushSize"
              :min="1"
              :max="30"
              :step="1"
              :ripple="false"
              density="compact"
              hide-details
              class="brush-slider"
            />
            <span class="text-caption ml-3 brush-value">{{ brushSize }}</span>
          </v-sheet>
          <v-sheet
            v-else-if="tool === 'polygon'"
            class="tool-options pa-2 px-4 d-flex align-center"
            rounded="pill"
            elevation="6"
          >
            <span class="text-caption">
              {{
                $gettext('Click to add points · click the first point to close')
              }}
              <strong class="ml-1">{{ polygonCount }}</strong>
            </span>
          </v-sheet>

          <!-- The finished-object exit: mask + class are set, so the natural
             next step is starting the next object — without traveling to
             the Objects panel (roast feedback). -->
          <v-tooltip
            v-if="canFinishObject"
            location="top"
            :text="
              $gettext(
                'Finishes this object — the next click on the image starts the next one',
              )
            "
          >
            <template v-slot:activator="{ props: tooltipProps }">
              <r-btn
                v-bind="tooltipProps"
                variant="accent"
                icon="mdi-check"
                @click="finishObject"
              >
                {{ $gettext('Object Done (Enter)') }}
              </r-btn>
            </template>
          </v-tooltip>
        </div>

        <!-- empty state -->
        <div
          v-if="!loading && images.length === 0 && !lockedBy"
          class="empty-state text-center"
        >
          <p class="text-body-1 mb-2">
            {{ $gettext('No images to label yet.') }}
          </p>
          <p class="text-body-2 text-medium-emphasis">
            {{ $gettext('Import images on the Labeling tab first.') }}
          </p>
        </div>
      </div>
    </div>

    <!-- lock overlay -->
    <!-- contained: scope the scrim to the editor card — covering the app
         header reads as the whole app being locked. -->
    <v-overlay
      :model-value="!!lockedBy"
      persistent
      contained
      class="align-center justify-center"
    >
      <v-card rounded="lg" max-width="420" class="pa-4 text-center">
        <v-icon icon="mdi-lock" size="large" color="warning" class="mb-2" />
        <div class="text-body-1 mb-4">{{ lockedBy }}</div>
        <div class="d-flex justify-center ga-2">
          <r-btn variant="outlined" @click="close">
            {{ $gettext('Back to Project') }}
          </r-btn>
          <r-btn variant="accent" @click="retryLock">
            {{ $gettext('Retry') }}
          </r-btn>
        </div>
      </v-card>
    </v-overlay>

    <!-- rotation first-open dialog -->
    <v-dialog v-model="rotationDialog" max-width="480" persistent>
      <v-card rounded="lg">
        <v-card-title>{{ $gettext('Label rotation angles?') }}</v-card-title>
        <v-card-text>
          {{
            $gettext(
              'Some projects need a rotation angle per object (around Z from top view). Projects using PCA pose estimation or circular parts can skip it. You can change this later in the Details panel.',
            )
          }}
          <!-- Angular part = orientation matters; round part = rotationally
               symmetric, no angle to label (roast feedback: show, not tell) -->
          <div class="d-flex ga-3 mt-4">
            <div class="rotation-example flex-grow-1 pa-3 text-center">
              <svg viewBox="0 0 96 72" width="96" height="72" class="mb-1">
                <g
                  transform="rotate(30 48 40)"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="24" y="30" width="48" height="20" rx="3" />
                </g>
                <g fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M48 40 L70 18" />
                  <path d="M63 17 L70 18 L69 25" />
                  <path d="M48 16 A24 24 0 0 1 65 23" stroke-dasharray="3 3" />
                </g>
              </svg>
              <div class="text-caption font-weight-medium">
                {{ $gettext('Rotation needed') }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ $gettext('Angular part — orientation matters') }}
              </div>
            </div>
            <div class="rotation-example flex-grow-1 pa-3 text-center">
              <svg viewBox="0 0 96 72" width="96" height="72" class="mb-1">
                <g fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="48" cy="40" r="22" />
                  <circle cx="48" cy="40" r="8" stroke-dasharray="3 3" />
                </g>
              </svg>
              <div class="text-caption font-weight-medium">
                {{ $gettext('Rotation not needed') }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ $gettext('Round part — every angle looks the same') }}
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <r-btn variant="outlined" @click="chooseRotation(false)">
            {{ $gettext('No Rotation') }}
          </r-btn>
          <r-btn variant="accent" @click="chooseRotation(true)">
            {{ $gettext('Label Rotation') }}
          </r-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style scoped>
.labeling-editor {
  /* Sized by the pinned dialog overlay (see LabelingImagesSection). */
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.rotation-example {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}
.editor-body {
  min-height: 0;
  /* Small displays: scroll instead of clipping the tool rail off-screen. */
  overflow-x: auto;
}
.canvas-area {
  position: relative;
  /* Keep the canvas usable on small displays — the tool rail alone needs
     ~60px; below this the editor body scrolls instead of clipping. */
  min-width: 320px;
  background: #0d2130;
}
.pager-pill {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 9999px;
  /* Opaque: the absolutely centered pager may sit above the status chip on
     narrow windows — it must cover, not blend. */
  background: rgb(var(--v-theme-surface));
}
.pager-centered {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
.tool-rail {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  z-index: 6;
  display: flex;
  flex-direction: column;
}
/* v-btn-toggle lays out horizontally — the rail is vertical. The group also
   imposes its own compact button metrics; match the standalone icon buttons
   below it (Undo/Reset are 40×40). */
.tool-toggle {
  flex-direction: column;
  height: auto;
}
/* The tooltip-activator span between group and button breaks the group's
   own sizing — size both explicitly to match the 40×40 standalone icon
   buttons below (Undo/Reset). !important: the group's height rule wins the
   cascade otherwise. */
.tool-toggle :deep(> span) {
  display: inline-flex;
  width: 40px;
  height: 40px;
}
.tool-toggle :deep(.v-btn) {
  border-radius: 8px;
  width: 40px !important;
  height: 40px !important;
}
.zoom-pill {
  position: absolute;
  left: 14px;
  bottom: 14px;
  z-index: 6;
}
.panel-toggle {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 6;
}
/* Narrow windows: the centered pager needs the room; the Unlock button
   already communicates the locked state. */
@media (max-width: 1100px) {
  .lock-chip {
    display: none;
  }
}
.zoom-value {
  min-width: 42px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.bottom-bar {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
}
.brush-slider {
  width: 140px;
}
/* The focus/press halo makes the whole pill look like it grows mid-drag. */
.brush-slider :deep(.v-slider-thumb__surface::before) {
  display: none;
}
.brush-value {
  min-width: 2ch;
  text-align: end;
  font-variant-numeric: tabular-nums;
}
.position-text {
  min-width: 56px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.empty-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fcf9f7;
}
.empty-state p {
  color: inherit;
}
</style>
